import {NO, UIColor, UITextView, YES} from "uicore-ts"
import {RHButton} from "./RHButton"
import {RHBadges} from "./Utils_slop/RHDesignTokens"


type RHFilterChipMode = "inactive" | "active" | "soft"
type RHFilterChipVisualState = "normal" | "hovered" | "highlighted" | "focused"
type RHFilterChipSize = "regular" | "large"
type RHFilterChipSizeMetrics = { fontSize: string, horizontalPadding: number, rowHeight: number }


/**
 * RHFilterChipButton — a flat, multi-select filter chip.
 *
 * Although it derives from RHButton, a chip is deliberately the opposite of the
 * glossy gradient button: no gradient, no drop shadow, no ripple, no hover lift.
 * Only the ACTIVE chip is filled (a light green tint — the site accent); inactive
 * chips are outline/ghost. A third "soft" mode gives the panel-opening
 * "Muud filtrid" / "Filtrid" chip a neutral tint while its panel is open, so it
 * reads as "showing more" without looking like a selected status filter.
 *
 * Active/open is driven explicitly by the owner via `chipActive` / `chipOpen`,
 * not by the button's own toggle machinery. The inherited state methods are
 * overridden to strip the gradient/shadow/lift and paint the flat decoration
 * instead (background + text colour come from `this.colors`, applied per state by
 * the UIButton colour specifier; the border, shadow and focus ring are applied
 * here).
 *
 * `chipSize` offers a slightly larger variant for views where the chip stands
 * alone (e.g. the lone "Filtrid" toggle on the procurer dashboard).
 */
export class RHFilterChipButton extends RHButton {


    _chipActive = NO
    _chipOpen = NO
    _size: RHFilterChipSize = "regular"


    constructor(elementID?: string) {
        super(elementID)

        // Chips never ripple — detach the ripple layer the RHButton constructor
        // created so the inherited state methods animate nothing visible.
        this._rippleView.removeFromSuperview()

        // Active/open is owner-driven, not toggled by interaction.
        this.isToggleable = NO

        // Cast to the concrete type so the grouped `style` initializer resolves
        // (the polymorphic `this["style"]` otherwise rejects it) — same pattern as
        // RHFilterPanelView. Size-dependent metrics (font, padding) are applied by
        // _applySizeMetrics below.
        ;(this as RHFilterChipButton).configureWithObject({
            style: {
                borderStyle: "solid",
                borderWidth: RHBadges.filterChip.borderWidth,
                borderRadius: RHBadges.filterChip.borderRadius,
                fontWeight: RHBadges.filterChip.fontWeight,
                // Replace the glossy RHButton box treatment with a flat surface; the
                // per-state decoration below sets the real border/shadow.
                padding: "0px",
                boxShadow: "none",
                backgroundImage: "none"
            }
        })

        if (this.titleLabel) {
            this.titleLabel.configureWithObject({
                isSingleLine: YES,
                style: { fontWeight: RHBadges.filterChip.fontWeight }
            })
            this.titleLabel.textAlignment = UITextView.textAlignment.center
        }

        this._applySizeMetrics()
        this._applyColorsForCurrentMode()
    }


    // ── Size ───────────────────────────────────────────────────────────────────

    get chipSize(): RHFilterChipSize {
        return this._size
    }

    set chipSize(value: RHFilterChipSize) {
        if (this._size === value) {
            return
        }
        this._size = value
        this._applySizeMetrics()
        this.setNeedsLayout()
    }

    // The row height the owner should give this chip's frame (so the box height
    // matches the font/padding of its size).
    get chipRowHeight(): number {
        return this._sizeMetrics().rowHeight
    }

    _sizeMetrics(): RHFilterChipSizeMetrics {
        if (this._size === "large") {
            return RHBadges.filterChip.large
        }
        return RHBadges.filterChip
    }

    // Font size + content padding follow the chip's size. The title lays out from
    // contentPadding (half the folded-in horizontal padding on each side), so the
    // chip width hugs the label at every size.
    _applySizeMetrics() {
        const metrics = this._sizeMetrics()
        this.contentPadding = metrics.horizontalPadding / 2
        ;(this as RHFilterChipButton).configureWithObject({
            style: { fontSize: metrics.fontSize }
        })
        if (this.titleLabel) {
            this.titleLabel.configureWithObject({
                style: { fontSize: metrics.fontSize }
            })
        }
    }


    // ── Owner-driven mode ─────────────────────────────────────────────────────

    get chipActive(): boolean {
        return this._chipActive
    }

    set chipActive(value: boolean) {
        if (this._chipActive === value) {
            return
        }
        this._chipActive = value
        this._applyColorsForCurrentMode()
    }

    get chipOpen(): boolean {
        return this._chipOpen
    }

    set chipOpen(value: boolean) {
        if (this._chipOpen === value) {
            return
        }
        this._chipOpen = value
        this._applyColorsForCurrentMode()
    }


    _currentMode(): RHFilterChipMode {
        if (this._chipActive) {
            return "active"
        }
        if (this._chipOpen) {
            return "soft"
        }
        return "inactive"
    }


    // Per-state background + text colours go through this.colors so the UIButton
    // colour specifier paints them; border/shadow are applied in the state methods.
    _applyColorsForCurrentMode() {
        const mode = this._currentMode()

        let textColor = RHBadges.filterChip.inactiveTextColor
        let backgroundNormal = RHBadges.filterChip.inactiveBackgroundColor
        let backgroundHover = RHBadges.filterChip.inactiveHoverBackgroundColor
        let backgroundPressed = RHBadges.filterChip.inactivePressedBackgroundColor

        if (mode === "active") {
            textColor = RHBadges.filterChip.activeTextColor
            backgroundNormal = RHBadges.filterChip.activeBackgroundColor
            backgroundHover = RHBadges.filterChip.activeHoverBackgroundColor
            backgroundPressed = RHBadges.filterChip.activePressedBackgroundColor
        }
        else if (mode === "soft") {
            textColor = RHBadges.filterChip.softTextColor
            backgroundNormal = RHBadges.filterChip.softBackgroundColor
            backgroundHover = RHBadges.filterChip.softHoverBackgroundColor
            backgroundPressed = RHBadges.filterChip.softHoverBackgroundColor
        }

        // Single property, no suitable helper (setVariant would force a variant) —
        // direct assignment is the correct choice here, then repaint.
        this.colors = {
            titleLabel: {
                normal: textColor,
                hovered: textColor,
                highlighted: textColor,
                focused: textColor,
                selected: textColor
            },
            background: {
                normal: backgroundNormal,
                hovered: backgroundHover,
                highlighted: backgroundPressed,
                focused: backgroundNormal,
                selected: backgroundPressed
            }
        }

        this.updateContentForCurrentState()
    }


    // ── Flat decoration (border / shadow / focus ring), applied after super ────

    _borderColorForState(state: RHFilterChipVisualState): UIColor {
        const mode = this._currentMode()

        if (mode === "active") {
            if (state === "hovered" || state === "highlighted") {
                return RHBadges.filterChip.activeHoverBorderColor
            }
            return RHBadges.filterChip.activeBorderColor
        }
        if (mode === "soft") {
            return RHBadges.filterChip.softBorderColor
        }
        if (state === "hovered" || state === "highlighted") {
            return RHBadges.filterChip.inactiveHoverBorderColor
        }
        return RHBadges.filterChip.inactiveBorderColor
    }


    _shadowForState(state: RHFilterChipVisualState): string {
        const mode = this._currentMode()

        if (mode === "active") {
            if (state === "hovered") {
                return RHBadges.filterChip.activeHoverShadow
            }
            return RHBadges.filterChip.activeShadow
        }
        if (mode === "soft") {
            return RHBadges.filterChip.softShadow
        }
        if (state === "hovered") {
            return RHBadges.filterChip.inactiveHoverShadow
        }
        return RHBadges.filterChip.inactiveShadow
    }


    _applyFlatDecorationForState(state: RHFilterChipVisualState) {
        let outline = "none"
        let outlineOffset = "0px"
        if (state === "focused") {
            outline = RHBadges.filterChip.focusOutline
            outlineOffset = RHBadges.filterChip.focusOutlineOffset
        }

        ;(this as RHFilterChipButton).configureWithObject({
            style: {
                backgroundImage: "none",
                boxShadow: this._shadowForState(state),
                borderStyle: "solid",
                borderWidth: RHBadges.filterChip.borderWidth,
                borderColor: this._borderColorForState(state).stringValue,
                outline: outline,
                outlineOffset: outlineOffset
            }
        })
    }


    // ── State overrides (super FIRST, then flatten) ───────────────────────────

    override updateContentForNormalState() {
        super.updateContentForNormalState()
        this._applyFlatDecorationForState("normal")
    }

    override updateContentForHoveredState() {
        super.updateContentForHoveredState()
        this._applyFlatDecorationForState("hovered")
    }

    override updateContentForHighlightedState() {
        super.updateContentForHighlightedState()
        this._applyFlatDecorationForState("highlighted")
    }

    override updateContentForSelectedState() {
        super.updateContentForSelectedState()
        this._applyFlatDecorationForState("highlighted")
    }

    override updateContentForFocusedState() {
        // A pointer click focuses the native button as well. Treating that focus
        // like keyboard focus leaves a green ring around a chip immediately after
        // it is deselected, which makes its visual state appear one click behind.
        // Keep the explicit focus treatment for keyboard navigation only; pointer
        // focus continues to use the hover/normal treatment that matches chipActive.
        if (!this.viewHTMLElement.matches(":focus-visible")) {
            if (this.hovered) {
                this.updateContentForHoveredState()
            }
            else {
                this.updateContentForNormalState()
            }
            return
        }
        super.updateContentForFocusedState()
        this._applyFlatDecorationForState("focused")
    }


    // ── Sizing — width hugs the label so the bar can pack + wrap chips ─────────

    override intrinsicContentWidth(constrainingHeight?: number): number {
        const horizontalPadding = this._sizeMetrics().horizontalPadding
        if (!this.titleLabel) {
            return horizontalPadding
        }
        return this.titleLabel.intrinsicContentWidth(constrainingHeight) + horizontalPadding
    }

}
